/**
 * Provides a hook for merging data from an overview and a high-resolution source.
 * 
 * Manages the spatial relationship between ranges and determines the merge strategy.
 * 
 * @packageDocumentation
 * @module useDataMerge
 * @public
 */

import { useMemo } from 'react';

// -- Core logic

/**
 * Configuration for data-agnostic operations (on timeseries)
 */
export interface MergeConfig<T, U> {
  getCoord: (item: T) => U;
  compare: (a: U, b: U) => number;
}

/**
 * Assessment outcomes of spatial relationship between ranges
 */
export type RangeAssessment =
  | 'H_SUBSET_O'
  | 'H_SUPERSET_O'
  | 'H_OVERLAP_O'
  | 'H_DISTINCT_O'
  | 'H_EQUALS_O';

/**
 * Assess the spatial relationship between ranges
 */
export function assessRelationship<U>(
  rangeO: [U, U],
  rangeH: [U, U],
  compare: (a: U, b: U) => number
): RangeAssessment {
  const hStartsBeforeO = compare(rangeH[0], rangeO[0]) < 0;
  const hEndsAfterO = compare(rangeH[1], rangeO[1]) > 0;
  const hStartsAfterOEnd = compare(rangeH[0], rangeO[1]) > 0;
  const hEndsBeforeOStart = compare(rangeH[1], rangeO[0]) < 0;
  const hIsTheSameAsO =
    compare(rangeH[0], rangeO[0]) === 0 && compare(rangeH[1], rangeO[1]) === 0;

  if (hIsTheSameAsO) return 'H_EQUALS_O';
  if (hStartsAfterOEnd || hEndsBeforeOStart) return 'H_DISTINCT_O';
  if (!hStartsBeforeO && !hEndsAfterO) return 'H_SUBSET_O';
  if (hStartsBeforeO && hEndsAfterO) return 'H_SUPERSET_O';
  return 'H_OVERLAP_O';
}

/**
 * Merge strategy based on the assessment
 */
export type MergeDecision =
  | 'REPLACE_WITH_H'
  | 'INJECT_H'
  | 'APPEND_H'
  | 'KEEP_O';

/**
 * Determines the merge strategy based on the assessment
 */
export function decideStrategy(assessment: RangeAssessment): MergeDecision {
  switch (assessment) {
    case 'H_SUPERSET_O':
      return 'REPLACE_WITH_H';
    case 'H_DISTINCT_O':
      return 'APPEND_H';
    case 'H_SUBSET_O':
    case 'H_OVERLAP_O':
      return 'INJECT_H';
    case 'H_EQUALS_O':
      return 'KEEP_O';
    default:
      return 'INJECT_H';
  }
}

/**
 * Orchestrates the merge decision on the data
 */
export function executeMerge<T, U>(
  overview: T[],
  highRes: T[],
  rangeH: [U, U],
  decision: MergeDecision,
  config: MergeConfig<T, U>
): T[] {
  switch (decision) {
    case 'REPLACE_WITH_H':
      return [...highRes];
    case 'APPEND_H':
      return performSort([...overview, ...highRes], config);
    case 'INJECT_H':
      return performInjection(overview, highRes, rangeH, config);
    case 'KEEP_O':
      return [...overview];
  }
}

// -- Helpers

function performInjection<T, U>(
  overview: T[],
  highRes: T[],
  rangeH: [U, U],
  config: MergeConfig<T, U>
): T[] {
  const { getCoord, compare } = config;

  // Filter out points from O that are covered by the range of H
  const filteredO = overview.filter((point) => {
    const coord = getCoord(point);
    return compare(coord, rangeH[0]) < 0 || compare(coord, rangeH[1]) > 0;
  });

  return performSort([...filteredO, ...highRes], config);
}

function performSort<T, U>(data: T[], config: MergeConfig<T, U>): T[] {
  return [...data].sort((a, b) =>
    config.compare(config.getCoord(a), config.getCoord(b))
  );
}

/**
 * Finds where the latest incoming batch starts within a merged timeseries array.
 *
 * Used to rebase API `row_index` values that are relative to the last fetch batch
 * onto the merged row array exposed by {@link useTimeseriesRangeData}.
 *
 * @param merged - Full merged dataset after overview/detail or refresh merge.
 * @param incoming - Rows returned by the latest API fetch batch.
 * @param getCoord - Extracts the sort/compare coordinate from one row.
 * @returns Index of the first merged row that belongs to `incoming`, or `0`.
 */
export function computeVisibleDataBeginIndex<T>(
  merged: readonly T[],
  incoming: readonly T[],
  getCoord: (item: T) => string
): number {
  if (incoming.length === 0) {
    return 0;
  }

  const incomingCoords = new Set(incoming.map(getCoord));
  const index = merged.findIndex((row) => incomingCoords.has(getCoord(row)));

  return index === -1 ? 0 : index;
}

// -- Hook

/**
 * React hook to manage the lifecycle of the merged dataset.
 */
export function useDataMerge<T, U>(
  overview: T[],
  highRes: T[],
  rangeO: [U, U],
  rangeH: [U, U],
  config: MergeConfig<T, U>
): {
  mergedData: T[];
  assessment: RangeAssessment;
  decision: MergeDecision;
} {
  const result = useMemo(() => {
    const assessment = assessRelationship(rangeO, rangeH, config.compare);
    const decision = decideStrategy(assessment);
    const mergedData = executeMerge(
      overview,
      highRes,
      rangeH,
      decision,
      config
    );

    return { mergedData, assessment, decision };
    // the linter is wrong: we care more about the content of config than the config object itself
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overview, highRes, rangeO, rangeH, config.compare, config.getCoord]);

  return result;
}
